import { css } from '@emotion/react';
import React, { forwardRef, ReactNode, useState, useRef, useEffect } from 'react';
import { theme } from 'styles/theme';

type TypeInput = {
	placeholder?: string;
	width?: number;
	name?: string;
	onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
	onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
	error?: boolean;
	isNumber?: boolean;
	isPassword?: boolean;
	icon?: ReactNode;
	sizeMedium?: boolean;
	underline?: boolean;
	autofocus?: boolean;
	alignRight?: boolean;
	textCenter?: boolean;
	debounceMs?: number;
	value?: string | number;
};

export const InputDebounced = forwardRef<HTMLInputElement, TypeInput>(
	(
		{
			placeholder,
			width,
			onChange,
			onBlur,
			name,
			error,
			isNumber,
			isPassword,
			icon,
			sizeMedium,
			underline,
			autofocus,
			alignRight,
			textCenter,
			debounceMs = 300,
			value,
		},
		ref,
	) => {
		// Internal state to show immediate input update
		const [internalValue, setInternalValue] = useState(value ?? '');
		const debounceTimer = useRef<number | null>(null);

		// Sync internalValue if value prop changes externally
		useEffect(() => {
			setInternalValue(value ?? '');
		}, [value]);

		const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
			const val = e.target.value;
			setInternalValue(val); // immediate UI update

			if (debounceTimer.current) {
				clearTimeout(debounceTimer.current);
			}

			// clone event with current target value to avoid React pooling issues
			const eventCopy = { ...e, target: { ...e.target, value: val } };

			debounceTimer.current = window.setTimeout(() => {
				onChange(eventCopy);
			}, debounceMs);
		};

		const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
			if (debounceTimer.current) {
				clearTimeout(debounceTimer.current);
				debounceTimer.current = null;
			}
			onChange(e); // flush on blur immediately
			if (onBlur) onBlur(e);
		};

		return (
			<div
				css={css`
					width: 100%;
					position: relative;

					${alignRight &&
					css`
						text-align: right;
					`}
				`}
			>
				{!!icon && (
					<div
						onClick={(e) => {
							e.preventDefault();
						}}
						css={css`
							position: absolute;
							width: 20px;
							height: 20px;
							line-height: 20px;

							color: ${theme.color.text['300']};

							top: 11px;
							left: 12px;

							${sizeMedium &&
							css`
								top: 7px;
							`}
						`}
					>
						{icon}
					</div>
				)}

				<input
					ref={ref}
					name={name}
					value={internalValue}
					onChange={handleChange}
					onBlur={handleBlur}
					id={name}
					type={isNumber ? 'number' : isPassword ? 'password' : 'text'}
					step={isNumber ? 'any' : undefined}
					placeholder={placeholder}
					autoFocus={autofocus}
					css={css`
						height: ${sizeMedium ? 36 : 44}px;

						${textCenter &&
						css`
							text-align: center;
						`}

						${underline
							? css`
									border: 0;
									border-bottom: 1px solid #eee;
									border-radius: 0;

									&:focus {
										outline: none;
									}
							  `
							: css`
									border: 1px solid #d4d5de;
									border-radius: 8px;
							  `}

            width: ${width ? width + 'px' : '100%'};
						padding: 12px;
						font-size: 14px;
						line-height: 20px;

						&:focus {
							outline: none;
							box-shadow: 0 0 0 2px ${theme.color.primary['400']};
							border-color: transparent;
						}

						${!!icon &&
						css`
							padding-left: 44px;

							${sizeMedium &&
							css`
								padding-left: 36px;
							`}
						`}

						::placeholder {
							color: #8c8fa6;
						}

						${!!error &&
						css`
							border-color: #ff6767;
						`}
					`}
				/>
			</div>
		);
	},
);
